import { type FC, type ReactElement, useEffect, useRef } from 'react';
import { useSpotlightContext } from './SpotlightProvider';

export type SpotlightProps = {
  /** Identifier referenced from `useSpotlight` step definitions. */
  id: string;
  /**
   * Exactly one element. The spotlight anchors to that element's root DOM
   * node. Wrap multiple siblings in a single `<div>` (or similar) before
   * passing them in.
   */
  children: ReactElement;
};

/**
 * Marks an element as a target for `useSpotlight`. Renders nothing of its
 * own — emits a `display: none` marker `<span>` immediately before the
 * child so we can resolve the child's DOM via `nextElementSibling` without
 * adding a wrapper box or forwarding refs through every shared component.
 */
export const Spotlight: FC<SpotlightProps> = ({ id, children }) => {
  const markerRef = useRef<HTMLSpanElement>(null);
  const { registerTarget, unregisterTarget } = useSpotlightContext();

  useEffect(() => {
    const target = markerRef.current?.nextElementSibling as HTMLElement | null;
    if (!target) return;
    registerTarget(id, target);
    return () => unregisterTarget(id, target);
  }, [id, registerTarget, unregisterTarget]);

  return (
    <>
      <span ref={markerRef} aria-hidden style={{ display: 'none' }} />
      {children}
    </>
  );
};
